/**
 * @fileoverview 웹 스토리지 유틸리티
 *
 * 브라우저의 sessionStorage/localStorage를 통합 관리합니다.
 */

/**
 * 브라우저 웹 스토리지(sessionStorage/localStorage) 통합 컨트롤러
 *
 * sessionStorage와 localStorage를 동일한 API로 관리할 수 있도록 제공합니다.
 *
 * @example
 * // localStorage에 저장
 * webStorageController.setItem({ type: 'local', key: 'token', value: 'abc123' });
 *
 * // sessionStorage에서 조회
 * const token = webStorageController.getItem({ type: 'session', key: 'token' });
 *
 * // 특정 키 삭제
 * webStorageController.removeItem({ type: 'local', key: 'token' });
 *
 * // 전체 스토리지 초기화
 * webStorageController.clear('session');
 */
export const webStorageController = {
  /**
   * 웹 스토리지에 항목 저장
   * @param type - 스토리지 타입 ('session' | 'local')
   * @param key - 저장할 키
   * @param value - 저장할 값 (문자열로 자동 변환됨)
   */
  setItem: ({ type, key, value }: { type: 'session' | 'local'; key: string; value: any }): void =>
    type === 'session' ? sessionStorage.setItem(key, value) : localStorage.setItem(key, value),
  /**
   * 웹 스토리지에서 항목 조회
   * @param type - 스토리지 타입 ('session' | 'local')
   * @param key - 조회할 키
   * @returns 저장된 값 또는 null (키가 없는 경우)
   */
  getItem: ({ type, key }: { type: 'session' | 'local'; key: string }): string | null =>
    (type === 'session' ? sessionStorage.getItem(key) : localStorage.getItem(key)) ?? null,
  /**
   * 웹 스토리지에서 항목 삭제
   * @param type - 스토리지 타입 ('session' | 'local')
   * @param key - 삭제할 키
   */
  removeItem: ({ type, key }: { type: 'session' | 'local'; key: string }): void =>
    type === 'session' ? sessionStorage.removeItem(key) : localStorage.removeItem(key),
  /**
   * 웹 스토리지 전체 초기화
   * @param type - 스토리지 타입 ('session' | 'local')
   */
  clear: (type: 'session' | 'local'): void =>
    type === 'session' ? sessionStorage.clear() : localStorage.clear(),
};
